import { jsx as _jsx } from "react/jsx-runtime";
import { ShippingCostType } from '@client/components/listingForm/ShippingProfileDialog';
import { useApiClient } from '@client/hooks/useApiClient';
import { useMinDuration } from '@client/hooks/useMinDuration';
import { callApi } from '@client/utils/apiUtils';
import { ReturnPolicyType } from '@heirloom/common/constants';
import { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
const ShopManagerContext = createContext(null);
export const useShopManager = () => {
    const ctx = useContext(ShopManagerContext);
    if (!ctx)
        throw new Error('useShopManager must be used inside ShopManagerProvider');
    return ctx;
};
const toFormShippingProfile = (p) => ({
    id: String(p.id),
    name: p.name,
    originZip: p.originZip,
    cost: p.flatShippingRateCents != null
        ? { type: ShippingCostType.FlatRate, cents: p.flatShippingRateCents }
        : { type: ShippingCostType.Free },
    minDays: p.shippingDaysMin,
    maxDays: p.shippingDaysMax,
});
const toFormReturnProfile = (p) => {
    const base = { id: String(p.id), name: p.name };
    if (p.policyType === ReturnPolicyType.NO_RETURNS) {
        return { ...base, policy: { type: ReturnPolicyType.NO_RETURNS } };
    }
    if (p.policyType === ReturnPolicyType.CUSTOM) {
        return {
            ...base,
            windowDays: p.returnWindowDays ?? undefined,
            policy: { type: ReturnPolicyType.CUSTOM, text: p.policyDescrRichText ?? '' },
        };
    }
    return {
        ...base,
        windowDays: p.returnWindowDays ?? undefined,
        policy: { type: ReturnPolicyType.STANDARD },
    };
};
export const ShopManagerProvider = ({ children }) => {
    const { shortId: shopShortId } = useParams();
    const apiClient = useApiClient();
    const [directFulfillment, setDirectFulfillment] = useState(true);
    const [shopCountryCode, setShopCountryCode] = useState(null);
    const [processingProfiles, setProcessingProfiles] = useState([]);
    const [shippingProfiles, setShippingProfiles] = useState([]);
    const [returnProfiles, setReturnProfiles] = useState([]);
    const [personalizationProfiles, setPersonalizationProfiles] = useState([]);
    const [rawProfilesLoading, setProfilesLoading] = useState(true);
    const profilesLoading = useMinDuration(rawProfilesLoading);
    useEffect(() => {
        if (!shopShortId)
            return;
        setProfilesLoading(true);
        callApi(apiClient.shopManager.getProfiles({ params: { shopId: shopShortId } })).then((result) => {
            if (result.error === null) {
                setDirectFulfillment(result.data.directFulfillment);
                setShopCountryCode(result.data.countryCode);
                setProcessingProfiles(result.data.processingProfiles.map((p) => ({
                    id: String(p.id),
                    name: p.name,
                    minDays: p.minDays,
                    maxDays: p.maxDays,
                })));
                setShippingProfiles(result.data.shippingProfiles.map(toFormShippingProfile));
                setReturnProfiles(result.data.returnProfiles.map(toFormReturnProfile));
                setPersonalizationProfiles(result.data.personalizationProfiles.map((p) => ({
                    id: String(p.id),
                    name: p.name,
                    costCents: p.costCents,
                    helperText: p.helperText,
                })));
            }
            setProfilesLoading(false);
        });
    }, [shopShortId]);
    return (_jsx(ShopManagerContext.Provider, { value: {
            directFulfillment,
            setDirectFulfillment,
            shopCountryCode,
            processingProfiles,
            shippingProfiles,
            returnProfiles,
            personalizationProfiles,
            profilesLoading,
        }, children: children }));
};
